import { catalogNameToFhir, PROGRAM_DEFINITION_KIND, PROTOCOL_DEFINITION_KIND, syncKey } from "@repo/api";
import {
  type ChangeProgramDefinitionAvailabilityInput,
  type CorrectProgramDefinitionInput,
  changeProgramDefinitionAvailabilityInputSchema,
  correctProgramDefinitionInputSchema,
  type DefineProgramDefinitionInput,
  defineProgramDefinitionInputSchema,
  type ReviseProgramDefinitionInput,
  reviseProgramDefinitionInputSchema,
} from "@repo/api/schemas";
import type { Collection, InitialQueryBuilder, UtilsRecord } from "@tanstack/db";
import { and, eq, ilike, inArray, max, or, toArray } from "@tanstack/react-db";
import { defineWrite } from "../actions/define-write";
import {
  programDefinitionDayHighWaterMark,
  programDefinitionSessionHighWaterMark,
} from "../fhir/program-definition-storage";
import type { PlanDefinitionRow } from "../fhir/types";
import type { ProgramDefinitionDto, ProgramDefinitionFilters } from "./program-definition";
import { projectIncludedProgramDefinition } from "./program-definition-projection";
import { projectProtocolDefinitionRow } from "./protocol-definition-projection";

export interface ProgramRow extends PlanDefinitionRow {
  optimisticProgramDefinition?: ProgramDefinitionDto;
  optimisticProgramMemberMarks?: ProgramMemberMarks;
}

interface ProgramMemberMarks {
  days: number;
  sessions: number;
}

export type PlanCollection = Collection<ProgramRow, string, UtilsRecord>;

export function buildProgramDefinitionRowsQuery(
  query: InitialQueryBuilder,
  collection: PlanCollection,
  filters: ProgramDefinitionFilters = {},
) {
  const status =
    filters.availability === undefined ? undefined : filters.availability === "Active" ? "active" : "retired";
  const search = filters.search?.trim();
  const latestPrograms = query
    .from({ candidate: collection })
    .where(({ candidate }) => eq(candidate.catalogModelKind, PROGRAM_DEFINITION_KIND))
    .groupBy(({ candidate }) => candidate.url)
    .select(({ candidate }) => ({ url: candidate.url, version: max(candidate.catalogVersion) }));
  let programs = query
    .from({ definition: collection })
    .join({ latestProgram: latestPrograms }, ({ definition, latestProgram }) => eq(definition.url, latestProgram.url))
    .where(({ definition, latestProgram }) =>
      and(
        eq(definition.catalogModelKind, PROGRAM_DEFINITION_KIND),
        eq(definition.catalogVersion, latestProgram.version),
      ),
    );
  if (status) programs = programs.where(({ definition }) => eq(definition.status, status));
  if (search) {
    programs = programs.where(({ definition }) =>
      or(ilike(definition.title, `%${search}%`), ilike(definition.description, `%${search}%`)),
    );
  }

  const latestProtocols = query
    .from({ candidate: collection })
    .where(({ candidate }) => eq(candidate.catalogModelKind, PROTOCOL_DEFINITION_KIND))
    .groupBy(({ candidate }) => candidate.url)
    .select(({ candidate }) => ({ url: candidate.url, version: max(candidate.catalogVersion) }));
  const currentProtocols = query
    .from({ protocol: collection })
    .join({ latestProtocol: latestProtocols }, ({ protocol, latestProtocol }) => eq(protocol.url, latestProtocol.url))
    .where(({ protocol, latestProtocol }) =>
      and(eq(protocol.catalogModelKind, PROTOCOL_DEFINITION_KIND), eq(protocol.catalogVersion, latestProtocol.version)),
    );

  return programs
    .orderBy(({ definition }) => definition.title, "asc")
    .select(({ definition }) => ({
      ...definition,
      relatedPlanDefinitions: toArray(
        currentProtocols
          .where(({ protocol }) =>
            and(
              eq(protocol.catalogViewScope, definition.catalogViewScope),
              inArray(protocol.url, definition.programProtocolUrls),
            ),
          )
          .select(({ protocol }) => ({ ...protocol })),
      ),
    }));
}

export function buildProgramDefinitionRowQuery(
  query: InitialQueryBuilder,
  collection: PlanCollection,
  definitionUrl: string | undefined,
) {
  if (!definitionUrl) return undefined;
  return buildProgramDefinitionRowsQuery(query, collection)
    .where(({ definition }) => eq(definition.url, definitionUrl))
    .findOne();
}

function optimisticId(url: string, version: number): string {
  let hash = 0;
  for (const character of url) hash = (Math.imul(31, hash) + character.charCodeAt(0)) | 0;
  return `optimistic-program-${Math.abs(hash)}-${version}`;
}

function currentProtocolRowsByUrl(collection: PlanCollection) {
  const current = new Map<string, ProgramRow>();
  for (const row of collection.state.values()) {
    if (!row.url || row.catalogModelKind !== PROTOCOL_DEFINITION_KIND) continue;
    const prior = current.get(row.url);
    if (!prior || row.catalogVersion > prior.catalogVersion) current.set(row.url, row);
  }
  return current;
}

function currentProtocolsByUrl(collection: PlanCollection) {
  return new Map(
    [...currentProtocolRowsByUrl(collection)].map(([url, row]) => [url, projectProtocolDefinitionRow(row)]),
  );
}

function memberNumber(id: string): number {
  return Number(id.slice(id.lastIndexOf("-") + 1));
}

function toDto(
  input: Pick<ReviseProgramDefinitionInput, "days">,
  spine: Omit<ProgramDefinitionDto, "days">,
  collection: PlanCollection,
  initialMarks: ProgramMemberMarks,
): { dto: ProgramDefinitionDto; marks: ProgramMemberMarks } {
  const protocols = currentProtocolsByUrl(collection);
  let dayHigh = Math.max(initialMarks.days, ...input.days.flatMap((day) => (day.id ? [memberNumber(day.id)] : [])));
  let sessionHigh = Math.max(
    initialMarks.sessions,
    ...input.days.flatMap((day) => day.sessions.flatMap((session) => (session.id ? [memberNumber(session.id)] : []))),
  );
  const dto: ProgramDefinitionDto = {
    ...spine,
    days: input.days.map((day, index) => {
      return {
        dayId: day.id ?? `day-${++dayHigh}`,
        dayNumber: index + 1,
        sessions: day.sessions.map((session) => {
          const protocol = protocols.get(session.protocol.definitionUrl);
          return {
            sessionId: session.id ?? `session-${++sessionHigh}`,
            timeWindow: session.timeWindow,
            protocolDefinitionUrl: session.protocol.definitionUrl,
            protocolName: protocol?.name ?? "Unavailable protocol",
            ...(protocol === undefined ? {} : { totalDurationMinutes: protocol.totalDurationMinutes }),
          };
        }),
      };
    }),
  };
  return { dto, marks: { days: dayHigh, sessions: sessionHigh } };
}

function toRow(dto: ProgramDefinitionDto, marks: ProgramMemberMarks): ProgramRow {
  return {
    resourceType: "PlanDefinition",
    id: optimisticId(dto.definitionUrl, dto.version),
    url: dto.definitionUrl,
    version: String(dto.version),
    date: dto.mintedAt,
    ...catalogNameToFhir(dto.name),
    ...(dto.description === undefined ? {} : { description: dto.description }),
    status: dto.availability === "Active" ? "active" : "retired",
    catalogViewScope: "catalog-definitions",
    catalogModelKind: PROGRAM_DEFINITION_KIND,
    catalogVersion: dto.version,
    definitionKind: "program",
    programProtocolUrls: [
      ...new Set(dto.days.flatMap((day) => day.sessions.map((session) => session.protocolDefinitionUrl))),
    ],
    optimisticProgramDefinition: dto,
    optimisticProgramMemberMarks: marks,
  };
}

function currentMemberMarks(row: ProgramRow, dto: ProgramDefinitionDto): ProgramMemberMarks {
  if (row.optimisticProgramMemberMarks) return row.optimisticProgramMemberMarks;
  return {
    days: programDefinitionDayHighWaterMark(row) ?? Math.max(0, ...dto.days.map((day) => memberNumber(day.dayId))),
    sessions:
      programDefinitionSessionHighWaterMark(row) ??
      Math.max(0, ...dto.days.flatMap((day) => day.sessions.map((session) => memberNumber(session.sessionId)))),
  };
}

function currentRow(collection: PlanCollection, url: string): ProgramRow & { id: string } {
  const row = [...collection.state.values()]
    .filter((candidate) => candidate.url === url && candidate.catalogModelKind === PROGRAM_DEFINITION_KIND)
    .sort((left, right) => right.catalogVersion - left.catalogVersion)[0];
  if (!row?.id) throw new Error(`Program Definition '${url}' is unavailable`);
  return { ...row, id: row.id };
}

export interface ProgramDefinitionActionDependencies {
  collection: () => PlanCollection;
  now?: () => string;
  persist: {
    define: Parameters<typeof defineWrite<DefineProgramDefinitionInput>>[0]["persist"];
    revise: Parameters<typeof defineWrite<ReviseProgramDefinitionInput>>[0]["persist"];
    correct: Parameters<typeof defineWrite<CorrectProgramDefinitionInput>>[0]["persist"];
    activate: Parameters<typeof defineWrite<ChangeProgramDefinitionAvailabilityInput>>[0]["persist"];
    deactivate: Parameters<typeof defineWrite<ChangeProgramDefinitionAvailabilityInput>>[0]["persist"];
  };
}

export function createProgramDefinitionActions(dependencies: ProgramDefinitionActionDependencies) {
  const now = dependencies.now ?? (() => new Date().toISOString());
  const define = defineWrite<DefineProgramDefinitionInput>({
    optimistic: (params) => {
      const input = defineProgramDefinitionInputSchema.parse(params);
      const collection = dependencies.collection();
      const optimistic = toDto(
        input,
        {
          definitionUrl: input.definitionUrl,
          version: 1,
          mintedAt: now(),
          name: input.name,
          ...(input.description === undefined ? {} : { description: input.description }),
          availability: "Active",
        },
        collection,
        { days: 0, sessions: 0 },
      );
      collection.insert(toRow(optimistic.dto, optimistic.marks));
    },
    persist: async (input, options) => {
      await dependencies.persist.define(input, options);
    },
  });
  const revise = defineWrite<ReviseProgramDefinitionInput>({
    optimistic: (params) => {
      const input = reviseProgramDefinitionInputSchema.parse(params);
      const collection = dependencies.collection();
      const stored = currentRow(collection, input.definitionUrl);
      const current =
        stored.optimisticProgramDefinition ??
        projectIncludedProgramDefinition({
          ...stored,
          relatedPlanDefinitions: [...currentProtocolRowsByUrl(collection).values()],
        });
      const optimistic = toDto(
        input,
        { ...current, version: input.expectedVersion + 1, mintedAt: now() },
        collection,
        currentMemberMarks(stored, current),
      );
      collection.insert(toRow(optimistic.dto, optimistic.marks));
    },
    persist: async (input, options) => {
      await dependencies.persist.revise(input, options);
    },
  });
  const correct = defineWrite<CorrectProgramDefinitionInput>({
    optimistic: (params) => {
      const input = correctProgramDefinitionInputSchema.parse(params);
      const collection = dependencies.collection();
      const current = currentRow(collection, input.definitionUrl);
      collection.update(syncKey(current), (draft) => {
        if (input.name !== undefined) Object.assign(draft, catalogNameToFhir(input.name));
        if (input.description === null) delete draft.description;
        else if (input.description !== undefined) draft.description = input.description;
        if (draft.optimisticProgramDefinition) {
          draft.optimisticProgramDefinition = {
            ...draft.optimisticProgramDefinition,
            ...(input.name === undefined ? {} : { name: input.name }),
            ...(input.description === undefined ? {} : { description: input.description ?? undefined }),
          };
        }
      });
    },
    persist: async (input, options) => {
      await dependencies.persist.correct(input, options);
    },
  });
  const availabilityAction = (availability: ProgramDefinitionDto["availability"]) =>
    defineWrite<ChangeProgramDefinitionAvailabilityInput>({
      optimistic: (params) => {
        const input = changeProgramDefinitionAvailabilityInputSchema.parse(params);
        const collection = dependencies.collection();
        const current = currentRow(collection, input.definitionUrl);
        collection.update(syncKey(current), (draft) => {
          draft.status = availability === "Active" ? "active" : "retired";
          if (draft.optimisticProgramDefinition) {
            draft.optimisticProgramDefinition = { ...draft.optimisticProgramDefinition, availability };
          }
        });
      },
      persist: async (input, options) => {
        await (availability === "Active"
          ? dependencies.persist.activate(input, options)
          : dependencies.persist.deactivate(input, options));
      },
    });
  return {
    defineProgramDefinition: define,
    reviseProgramDefinition: revise,
    correctProgramDefinition: correct,
    activateProgramDefinition: availabilityAction("Active"),
    deactivateProgramDefinition: availabilityAction("Inactive"),
  };
}
